import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth } from '../lib/auth-context';

/**
 * Gate wrapper for any route that requires an authenticated operator.
 *
 * Three branches:
 *   - `loading`        → minimal spinner (avoids flashing /login on
 *     reload while /auth/me is still in flight)
 *   - `unauthenticated`→ redirect to /login, preserving the requested
 *     path in `state.from` so LoginPage can bounce back after success
 *   - `authenticated`  → render children
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-800">
        <div className="text-sm text-gray-500">Loading…</div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // OCP-755 - a super_admin without a second factor sets it up first; every admin route answers 403 until then.
  if (user?.mfa_enrollment_required && location.pathname !== '/security') {
    return <Navigate to="/security" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
